import { Skeleton } from "@/components/ui/skeleton";
import { downloadPdf, type Booking as PdfBooking } from "@/lib/downloadPdf";
import {
  parseAppointmentTimeAsEthiopiaLocal,
  formatTimeGregorianInEthiopia,
  formatTimeEthiopianWithSuffix,
  formatDateGregorianInEthiopia,
  formatDateEthiopian,
} from "@/lib/ethiopian-date-conversion";
import { useQuery } from "@tanstack/react-query";
import { useRouter, useSearchParamsCompat } from "@/lib/router-helpers";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";
import BookingSuccessSkeleton from "@/components/success-skeleton";

type Booking = {
  id: string;
  branch: string;
  category: string;
  datetime: string;
  cnr: string;
  bookingCode?: string;
  userName?: string;
  userMobile?: string;
  firstName?: string;
  lastName?: string;
  appointmentTime?: string;
  status: string;
  raw?: any;
};

function formatAppointmentDateTime(iso: string): string {
  const d = parseAppointmentTimeAsEthiopiaLocal(iso)
  const time = formatTimeGregorianInEthiopia(d)
  const date = formatDateGregorianInEthiopia(d)
  return `${time} : ${date}`
}

export default function SuccessPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParamsCompat();
  const cnr = searchParams?.get('merch_order_id') || searchParams?.get('appointment_id');
  const router = useRouter();
  const [qrLoaded, setQrLoaded] = useState(false);

  const fetchAppointmentDetails = async (cnr: string) => {
    const token = Cookies.get("qetero.token");
    const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || ''
    const response = await fetch(`${apiUrl}/v1/payments/appointment/${cnr}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) throw new Error("Failed to fetch appointment details");
    return response.json();
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['appointmentDetails', cnr],
    queryFn: () => fetchAppointmentDetails(cnr!),
    enabled: !!cnr,
  });

  useEffect(() => {
    if (!isLoading && (!data || error)) {
      router.push("/");
    }
  }, [data, error, isLoading, router]);

  useEffect(() => {
    setQrLoaded(false);
  }, [data?.cnr]);

  if (isLoading) {
    return <BookingSuccessSkeleton />;
  }

  if (error) {
    return <div>{t("Error loading appointment tiket")}</div>;
  }

  if (!data) return null;

  const userName = [data["user.firstname"], data["user.lastname"]]
    .filter(Boolean)
    .join(" ") || undefined
  const userMobile = data["user.mobile"] as string | undefined
  const firstName = data["user.firstname"] as string | undefined
  const lastName = data["user.lastname"] as string | undefined
  const branchName = data["branch_service.branch.name"] || data?.branch_service?.branch?.name || ""
  const serviceName = data["branch_service.service.name"] || data?.branch_service?.service?.name || ""
  const appointmentDate = parseAppointmentTimeAsEthiopiaLocal(data.appointment_time)
  const formattedDatetime = formatAppointmentDateTime(data.appointment_time)
  const timeLocal = formatTimeEthiopianWithSuffix(appointmentDate)
  const timeGregorian = formatTimeGregorianInEthiopia(appointmentDate)
  const formattedDateLocal = formatDateEthiopian(appointmentDate)
  const formattedDateGC = formatDateGregorianInEthiopia(appointmentDate)
  const isAmharic = i18n.language?.toLowerCase().startsWith("am")
  const dateTop = isAmharic ? formattedDateLocal : formattedDateGC
  const dateBottom = isAmharic ? formattedDateGC : formattedDateLocal
  const timeTop = isAmharic ? timeLocal : timeGregorian
  const timeBottom = isAmharic ? timeGregorian : timeLocal

  const selected: Booking = {
    id: data.id,
    branch: branchName,
    category: serviceName,
    datetime: formattedDatetime,
    cnr: data.cnr,
    bookingCode: data.booking_code,
    userName,
    userMobile,
    firstName,
    lastName,
    appointmentTime: data.appointment_time,
    status: data.status,
    raw: data,
  };

  const pdfStatus: PdfBooking["status"] = (selected.status === "confirmed" || selected.status === "upcoming")
    ? "upcoming"
    : selected.status === "completed"
    ? "completed"
    : "expired";

  return (
    <div className="flex justify-center items-center">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
        <div className="flex justify-center mb-4">
          <div className="text-pink-600 font-extrabold">CHECHE</div>
          <div className="ml-2 text-slate-800 font-semibold">Qetero</div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex justify-between">
            <div className="text-sm text-gray-500 font-medium">{t("User")}:</div>
            <div className="text-sm font-semibold">
              {selected.userName ||
                selected.raw?.createdBy?.name ||
                selected.raw?.created_by?.name ||
                selected.raw?.userName ||
                "—"}
            </div>
          </div>

          <div className="flex justify-between">
            <div className="text-sm text-gray-500 font-medium">{t("MobileNumber")}:</div>
            <div className="text-sm font-semibold">{selected.userMobile || "—"}</div>
          </div>

          <div className="flex justify-between">
            <div className="text-sm text-gray-500 font-medium">{t("branchName")}:</div>
            <div className="text-sm font-semibold">{t(selected.branch)}</div>
          </div>

          <div className="flex justify-between">
            <div className="text-sm text-gray-500 font-medium">{t("service")}:</div>
            <div className="text-sm font-semibold">{t(selected.category)}</div>
          </div>

          <div className="flex justify-between">
            <div className="text-sm text-gray-500 font-medium">{t("bookingDate")}</div>
            <div className="text-right">
              <div className="text-sm font-bold text-gray-900">{dateTop}</div>
              <div className="text-xs text-gray-500">{dateBottom}</div>
            </div>
          </div>

          <div className="flex justify-between">
            <div className="text-sm text-gray-500 font-medium">{t("bookingTime")}</div>
            <div className="text-right">
              <div className="text-sm font-bold text-gray-900">{timeTop}</div>
              <div className="text-xs text-gray-500">{timeBottom}</div>
            </div>
          </div>

          <div className="flex justify-between">
            <div className="text-sm text-gray-500 font-medium">CNR:</div>
            <div className="text-sm font-semibold">{selected.cnr || "—"}</div>
          </div>
        </div>

        <hr className="my-2" />

        <div className="flex justify-center my-4">
          <div className="w-48 h-48 flex items-center justify-center">
            {!qrLoaded && <Skeleton className="w-48 h-48" />}
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(
                selected.cnr ?? selected.id
              )}`}
              alt="qr"
              className={`w-48 h-48 object-contain ${qrLoaded ? "" : "hidden"}`}
              onLoad={() => setQrLoaded(true)}
              onError={() => setQrLoaded(true)}
            />
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mb-4">
          {t("qrCodeInstruction")}
        </p>

        <div className="flex gap-3">
          <button
            className="flex-1 py-2 rounded-full border border-pink-400 text-pink-500 font-semibold"
            onClick={() => router.push('/organization/dars')}
          >
           {t("Back to home")}
          </button>
          <button
            className="flex-1 py-2 rounded-full bg-pink-500 text-white font-semibold"
            onClick={() => downloadPdf({ ...selected, status: pdfStatus })}
          >
            {t("downloadPdf")}
          </button>
        </div>
      </div>
    </div>
  );
}